import { MULTI_CLIENT } from '@/features/types';
import { useUserClientContext, type ClientContext } from '@/app/clientContext';
import {
  useClientTerminology,
  type ClientTerminology,
} from '@/views/list/NetworkTerms';
import type {
  NetworkGroupData as NetworkGroup,
  NetworkGroupType,
} from '@/generated/types/navigator/types.gen';
import type { NetworkGroupConfig } from '@/features/types';

export type LabelVersion =
  | 'singularTitle'
  | 'singularInline'
  | 'pluralTitle'
  | 'pluralInline';

export const UNGROUPED: NetworkGroup = {
  id: 6,
  name: 'span',
  displayName: 'Span',
  singularInline: 'span',
  singularTitle: 'Span',
  pluralInline: 'spans',
  pluralTitle: 'Spans',
  types: [],
};

/**
 * Retrieves the display label for a network group using a string key.
 * If the key does not exist in networkGroupNames, it logs an error
 * and returns the key itself as a fallback.
 */
export const getNetworkGroupLabel = (
  group: NetworkGroup | null | undefined,
  context: ClientContext,
  intl: ClientTerminology,
  style: LabelVersion = 'singularTitle'
): string => {
  if (!group?.name) {
    return '';
  }

  const result = group?.[style];

  if (result) {
    /**
     * Multi-client-specific terminology: appends "& segment"
     */
    const multiClientCircuitAddition =
      context.clientType === MULTI_CLIENT && group.name === 'circuit'
        ? intl.formatMessage({ id: 'filter.level2TransmissionSegment' })
        : '';

    if (!multiClientCircuitAddition) {
      return result;
    }

    // Do some extra processing for multi-client circuits
    // This is a weird case, so we're going to do it here
    // for now, but this should be unified to be more consistent
    // with the rest of the code at some point.
    const casedMultiClientCircuitAddition =
      style === 'singularTitle' || style === 'pluralTitle'
        ? multiClientCircuitAddition
        : multiClientCircuitAddition.toLocaleLowerCase();

    const formattedMultiClientCircuitAddition =
      style === 'pluralInline' || style === 'pluralTitle'
        ? `${casedMultiClientCircuitAddition}s`
        : casedMultiClientCircuitAddition;

    return `${result} & ${formattedMultiClientCircuitAddition}`;
  }

  // This shouldn't happen, but better to return a fallback
  return group.displayName || group.name;
};

export const useNetworkGroupLabel = (
  group: NetworkGroup | null | undefined,
  position: LabelVersion = 'singularTitle'
): string => {
  const { context } = useUserClientContext();
  const intl = useClientTerminology();
  return getNetworkGroupLabel(group, context, intl, position);
};

export const hasNetworkGroupType = (
  group: NetworkGroup,
  type: NetworkGroupType
): boolean => group.types?.includes(type) ?? false;

/**
 * Retrieves the internal 'name' identifier for a network group by its ID from the provided mappings.
 */
// TODO rename mappings to networkGroup
export const getGroupNameFromId = (
  id: string,
  mappings: NetworkGroup[] | undefined
): string | undefined => {
  const mapping = mappings?.find((mapping) => mapping.id.toString() === id);
  return mapping ? mapping.name : undefined;
};

/**
 * Filters groups that should appear in filter components: groups with inlined
 * values, plus `searchable` groups whose values are queried on demand.
 */
export const getFilterableGroups = (groups: NetworkGroup[]): NetworkGroup[] => {
  return groups.filter((group) => {
    return (
      (!group.category || group.category === 'filter') &&
      (group.searchable === true || (group.values ?? []).length > 0)
    );
  });
};

/**
 * Filters groups that should appear in group-by dropdowns
 */
export const getGroupableGroups = (groups: NetworkGroup[]): NetworkGroup[] => {
  return groups.filter((group) => {
    return !group.category || group.category === 'group';
  });
};

/** TODO add docstring and tests
 *
 */
export const getNetworkGroupConfigs = (
  groups: NetworkGroup[]
): NetworkGroupConfig[] => {
  return groups.flatMap((group) =>
    (group.types ?? []).map((type) => ({
      type,
      networkGroupId: group.id,
    }))
  );
};
